import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { Loader2, Plus, Trash2 } from "lucide-react";

import { Button } from "../ui/button";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { getPublicFileUrl } from "../../config";

// Painel de figurinhas do atendimento.
//
// O que o servidor guarda é a figurinha já convertida (webp 512): a pessoa
// escolhe qualquer foto do computador e a conversão acontece no envio, porque
// o WhatsApp recusa figurinha que não seja webp.
const FigurinhaPicker = ({ aberto, aoFechar, ticketId, desabilitado }) => {
	const [figurinhas, setFigurinhas] = useState([]);
	const [carregando, setCarregando] = useState(false);
	const [enviando, setEnviando] = useState(null);
	const [subindo, setSubindo] = useState(false);
	const painelRef = useRef(null);
	const arquivoRef = useRef(null);

	useEffect(() => {
		if (!aberto) return;

		(async () => {
			setCarregando(true);
			try {
				const { data } = await api.get("/figurinhas");
				setFigurinhas(Array.isArray(data) ? data : []);
			} catch (err) {
				toastError(err);
			}
			setCarregando(false);
		})();
	}, [aberto]);

	// Clicar fora fecha, como o seletor de emoji ao lado.
	useEffect(() => {
		if (!aberto) return undefined;

		const aoClicarFora = (e) => {
			if (painelRef.current && !painelRef.current.contains(e.target)) {
				aoFechar();
			}
		};

		document.addEventListener("mousedown", aoClicarFora);
		return () => document.removeEventListener("mousedown", aoClicarFora);
	}, [aberto, aoFechar]);

	const enviar = async (figurinha) => {
		setEnviando(figurinha.id);
		try {
			await api.post(`/figurinhas/${figurinha.id}/enviar`, { ticketId });
			aoFechar();
		} catch (err) {
			toastError(err);
		}
		setEnviando(null);
	};

	const adicionar = async (e) => {
		const arquivos = Array.from(e.target.files || []);

		if (arquivos.length === 0) return;

		const dados = new FormData();
		arquivos.forEach((arquivo) => dados.append("medias", arquivo));

		setSubindo(true);
		try {
			const { data } = await api.post("/figurinhas", dados);
			setFigurinhas((atual) => [...(data || []), ...atual]);
			toast.success(
				arquivos.length > 1 ? "Figurinhas criadas." : "Figurinha criada.",
			);
		} catch (err) {
			toastError(err);
		}
		setSubindo(false);
		// Sem isto, escolher o mesmo arquivo de novo não dispara o onChange.
		e.target.value = "";
	};

	const remover = async (figurinha, e) => {
		e.stopPropagation();

		try {
			await api.delete(`/figurinhas/${figurinha.id}`);
			setFigurinhas((atual) => atual.filter((f) => f.id !== figurinha.id));
		} catch (err) {
			toastError(err);
		}
	};

	if (!aberto) return null;

	return (
		<div
			ref={painelRef}
			className="absolute bottom-full left-2 right-2 z-20 mb-2 rounded-lg border bg-popover p-3 shadow-lg sm:right-auto sm:w-[340px]"
		>
			<div className="mb-2 flex items-center justify-between">
				<span className="text-sm font-medium">Figurinhas</span>
				<input
					ref={arquivoRef}
					type="file"
					multiple
					accept="image/png,image/jpeg,image/webp,image/gif"
					className="hidden"
					onChange={adicionar}
				/>
				<Button
					size="sm"
					variant="ghost"
					disabled={subindo}
					onClick={() => arquivoRef.current?.click()}
				>
					{subindo ? (
						<Loader2 className="h-4 w-4 animate-spin" />
					) : (
						<Plus className="h-4 w-4" />
					)}
					Nova
				</Button>
			</div>

			{carregando ? (
				<div className="flex h-24 items-center justify-center">
					<Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
				</div>
			) : figurinhas.length === 0 ? (
				// Estado vazio que diz o próximo passo: uma grade vazia deixaria
				// a pessoa achando que o painel está quebrado.
				<p className="py-6 text-center text-sm text-muted-foreground">
					Nenhuma figurinha ainda.
					<br />
					Use <strong>Nova</strong> para transformar uma imagem em
					figurinha, ou salve uma que o cliente mandar pelo menu da
					mensagem.
				</p>
			) : (
				<div className="grid max-h-64 grid-cols-4 gap-2 overflow-y-auto">
					{figurinhas.map((figurinha) => (
						<div key={figurinha.id} className="group relative">
							<button
								type="button"
								disabled={desabilitado || enviando === figurinha.id}
								onClick={() => enviar(figurinha)}
								title={figurinha.name}
								className="flex h-16 w-full items-center justify-center rounded-md border bg-background p-1 hover:bg-accent disabled:opacity-50"
							>
								{enviando === figurinha.id ? (
									<Loader2 className="h-4 w-4 animate-spin" />
								) : (
									<img
										src={getPublicFileUrl(figurinha.mediaUrl)}
										alt={figurinha.name}
										loading="lazy"
										className="max-h-full max-w-full object-contain"
									/>
								)}
							</button>
							<button
								type="button"
								onClick={(e) => remover(figurinha, e)}
								aria-label="Apagar figurinha"
								title="Apagar figurinha"
								className="absolute -right-1 -top-1 hidden h-5 w-5 items-center justify-center rounded-full bg-destructive text-destructive-foreground group-hover:flex"
							>
								<Trash2 className="h-3 w-3" />
							</button>
						</div>
					))}
				</div>
			)}
		</div>
	);
};

export default FigurinhaPicker;
